/**
 * `AW1` · El arranque.
 *
 * Resuelve la configuración de la instancia ANTES de montar nada. La aplicación
 * no puede renderizar una tabla sin saber a qué `admin-web` preguntarle, y
 * montar primero para descubrirlo después dejaría un estado intermedio en el que
 * la pantalla se ve rota sin decir por qué.
 */

import { StrictMode } from "react"
import { createRoot } from "react-dom/client"
import { App } from "./App.tsx"
import { configuracion } from "./configuracion.ts"
import { instalarLaDeclaracion } from "./zonas.ts"
import "./estilos.css"

const contenedor = document.getElementById("root")
if (contenedor === null) {
  throw new Error("Falta el elemento #root en index.html.")
}

const raiz = createRoot(contenedor)

/**
 * Una configuración que no carga se muestra COMO LO QUE ES.
 *
 * El modo de falla que esto evita: `configuracion.json` mal subido a un bucket
 * nuevo, y una pantalla en blanco cuya única pista está en la consola del
 * navegador. Quien opera esto no la va a abrir. El mensaje nombra el archivo y
 * lo que falta, que es lo único accionable.
 */
configuracion.cargar().then(
  (config) => {
    /*
     * ⭐⭐ `[22/9/2026]` · **LA DECLARACIÓN SE INSTALA ANTES DE MONTAR**, y acá es
     * el único lugar donde eso se puede hacer una sola vez.
     *
     * `zonas.ts` dejó de hornear los brazos y el nombre de la instancia: los LEE
     * de la declaración. Pero `canonicalizar()` de `rutas.ts` es una función pura
     * que corre fuera de React, así que la declaración no puede vivir sólo en un
     * contexto: tiene que estar puesta **antes** de que se evalúe la primera
     * ruta. Esta costura ya existía y ya resolvía exactamente este problema —«la
     * aplicación no puede renderizar una tabla sin saber a qué `admin-web`
     * preguntarle»—; lo único que se agrega es qué más se resuelve acá.
     *
     * ⚠️ El camino de error de abajo **no la instala, y es correcto**: si la
     * configuración no se pudo leer no hay declaración que instalar, y lo que se
     * dibuja no es la consola sino el cartel que dice por qué. Ver `zonas.ts`
     * ⟦la-declaracion-se-instala-antes-de-montar⟧.
     */
    instalarLaDeclaracion(config)
    raiz.render(
      <StrictMode>
        <App config={config} />
      </StrictMode>,
    )
  },
  (error: unknown) => {
    raiz.render(
      <StrictMode>
        <main className="arranque-fallido" role="alert">
          <h1>No se pudo iniciar el Management Tool</h1>
          <p>{error instanceof Error ? error.message : String(error)}</p>
        </main>
      </StrictMode>,
    )
  },
)
